import { useEffect, useId, useMemo, useRef } from 'react';
import { Button, Checkbox, Flex, Spin, Tooltip, Typography } from 'antd';
import { LuCalculator, LuExpand, LuSettings, LuShrink, LuTriangleAlert } from 'react-icons/lu';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';

import { useProject } from '@contexts/Project.context';
import { useWebGLVectorLayer } from '@hooks/useWebGLVectorLayer';
import useIsSidebarOpen from '@hooks/useIsSidebarOpen';
import { createFeature } from '@utils/map/helpers';
import { COLLECTION_LAYER_ID } from '@utils/constants';
import { useCollectionStore } from '@stores/useCollectionStore';

import {
	hexToRgba,
	DEFAULT_PRESET,
} from '@routes/userRoutes/projects/singleProject/components/modifyLayers/hexagonColorFilter/matrixFilter/matrixClassification';

import useCollectionFilterBody from './hooks/useCollectionFilterBody';
import useCollectionHexagons from './hooks/useCollectionHexagons';
import useHexagonClassification from './hooks/useHexagonClassification';
import useLayerVisibilityPreference from './hooks/useLayerVisibilityPreference';
import CollectionHexagonTooltip from './components/CollectionHexagonTooltip';
import CollectionHexagonPanel from './components/CollectionHexagonPanel';
import CollectionHexagonSettings from './components/CollectionHexagonSettings';
import CollectionHexagonColorFilter from './components/colorFilter/CollectionHexagonColorFilter';
import { COLLECTION_SIDEBAR_IDS } from './constants';

const { Text } = Typography;

const LAYER_ID = COLLECTION_LAYER_ID.HEXAGON;

// The base hue comes from `hexagonColor` (picked in Hexagon Settings, saved to
// the collection). A single translucent hue with a density-driven opacity ramp
// reads clearly on the dark map without competing with the detection shapes.

// Opacity ramp endpoints for the density heat (0 → sparsest, 1 → densest cell).
const HEAT_MIN_ALPHA = 0.12;
const HEAT_MAX_ALPHA = 0.75;

// Zoom range over which the fill fades out as the user zooms in, mirroring
// the model-based HexagonLayer's getZoomBasedOpacity (see ../HexagonLayer.jsx).
// Keeps the density overlay readable while zoomed out without obscuring
// individual detections/boundary once zoomed in close.
const ZOOM_FADE_MIN_ZOOM = 15;
const ZOOM_FADE_MAX_ZOOM = 22;

// Sits just below the boundary (101) and detection shapes (102–104) so the
// translucent heat fill underlays them rather than masking the detections.
const Z_INDEX = 100;

// `['zoom']` is a WebGL-only expression the renderer re-evaluates every
// frame, so this fade applies live as the user zooms with no extra
// listener/style-recompile effect (unlike the model layer's imperative
// change:resolution handler, which is needed there because it uses
// per-feature setStyle on a plain, non-expression-styled layer).
const ZOOM_FADE_FACTOR = [
	'interpolate',
	['linear'],
	['zoom'],
	ZOOM_FADE_MIN_ZOOM,
	1,
	ZOOM_FADE_MAX_ZOOM,
	0,
];

// Density heat is encoded per feature as a normalized `heat` property (0–1) and
// mapped to fill opacity declaratively — no per-feature setStyle needed, which
// keeps the layer WebGL and consistent with the other collection layers.
// NOTE: only *numeric* `get` expressions are safe here. WebGL packs style
// attributes as floats, so a boolean `get` (e.g. for selection) breaks shader
// compilation and freezes the whole map — selection is handled on a separate
// plain vector layer instead (see SELECTED_STYLE).
//
// Only the hue is user-controlled: opacity stays derived from density (and the
// zoom fade), so the ramp keeps its meaning whichever color is chosen.
export const buildHeatStyle = hex => {
	const { r, g, b } = hexToRgba(hex, 1);
	return {
		'fill-color': [
			'color',
			r,
			g,
			b,
			[
				'*',
				['interpolate', ['linear'], ['get', 'heat'], 0, HEAT_MIN_ALPHA, 1, HEAT_MAX_ALPHA],
				ZOOM_FADE_FACTOR,
			],
		],
		'stroke-color': `rgba(${r}, ${g}, ${b}, 0.9)`,
		'stroke-width': 1,
	};
};

// Selected-cell outline, drawn on its own plain VectorLayer (one feature),
// decoupled from the WebGL heat layer. A bright ring above the other layers.
const SELECTED_STYLE = new Style({
	stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.95)', width: 3 }),
});
const SELECTED_LAYER_ID = COLLECTION_LAYER_ID.HEXAGON_SELECTED;
const SELECTED_Z_INDEX = 110;

/** hexToRgba → a WebGL-accepted "rgba(r, g, b, a)" string. */
const toRgba = (hex, alpha) => {
	const { r, g, b, a } = hexToRgba(hex, alpha);
	return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/**
 * Builds the classified WebGL style: a `match` on the per-feature `matrixClass`
 * property → each class's fill (its own opacity) and a stronger stroke. The
 * fallback (grey) covers any feature not assigned a class. This mirrors the
 * declarative `match` pattern the other collection layers use, keeping the layer
 * WebGL-safe (only string/number gets — no booleans).
 *
 * @param {Record<string, { id, color, opacity }>} results - per-class results
 *   from useHexagonClassification (user classes + below_minimum/unclassified)
 */
const buildClassifiedStyle = results => {
	const entries = Object.values(results);
	// OpenLayers' `match` expression requires at least one case pair before the
	// fallback — with no classes yet (e.g. results still empty while cells are
	// loading) fall back to a plain color instead of an empty `match`, which
	// WebGL rejects.
	if (!entries.length) {
		return {
			'fill-color': 'rgba(100, 100, 100, 0.3)',
			'stroke-color': 'rgba(100, 100, 100, 0.6)',
			'stroke-width': 1,
		};
	}
	const fillPairs = entries.flatMap(r => [r.id, toRgba(r.color, r.opacity)]);
	const strokePairs = entries.flatMap(r => [r.id, toRgba(r.color, 0.95)]);
	return {
		'fill-color': ['match', ['get', 'matrixClass'], ...fillPairs, 'rgba(100, 100, 100, 0.3)'],
		'stroke-color': [
			'match',
			['get', 'matrixClass'],
			...strokePairs,
			'rgba(100, 100, 100, 0.6)',
		],
		'stroke-width': 1,
	};
};

/**
 * Renders the collection's detection density as hexagon cells on the map, with a
 * show/hide toggle. Mirrors the model-based hexagon layer for the collection
 * stack, sourced from POST /collections/{uuid}/hexagons.
 *
 * The filter body is built from the collection's *persisted* filters + the
 * currently visible objects, so the hexagons agree with the detection list and
 * only re-aggregate when filters are applied or visibility changes (see
 * buildHexagonFilterBody / useCollectionHexagons). While a re-aggregation is in
 * flight the toggle is disabled and a spinner is shown, and the previous cells
 * stay on the map (keepPreviousData) rather than flickering empty.
 *
 * The row is a thin control — a visibility toggle plus three buttons, each
 * opening one of the sidebars this component mounts: settings, price estimation
 * (classification), and details.
 *
 * @param {object} props
 * @param {string} props.collectionUuid - the collection UUID
 * @param {string} props.collectionName - the collection's display name, used
 *   as the exported report's title (see CollectionHexagonColorFilter's export)
 * @param {Array<{key: string, savedFilters?: object}>} props.objects - collection
 *   objects with their persisted per-object filters
 * @param {object} props.savedCollectionFilters - persisted collection-level filters
 */
const CollectionHexagonLayer = ({
	collectionUuid,
	collectionName,
	objects,
	savedCollectionFilters,
}) => {
	const { mapObject, project, setActiveSidebar, removeFromActiveSidebars } = useProject();

	const { uuid: projectUuid } = project ?? {};

	const resolution = useCollectionStore(s => s.hexagonResolution);
	const selectedCellId = useCollectionStore(s => s.selectedHexagonCellId);
	const setSelectedHexagonCellId = useCollectionStore(s => s.setSelectedHexagonCellId);
	// The saved colour, plus the settings panel's unsaved pick so a colour
	// previews on the map as it is chosen.
	const hexagonColor = useCollectionStore(s => s.hexagonColor);
	const hexagonColorDraft = useCollectionStore(s => s.hexagonColorDraft);
	// Active matrix/simple color-filter (null = heat mode). Owned by the
	// color-filter panel; read here to color the cells by class.
	const classificationFilter = useCollectionStore(s => s.hexagonClassificationFilter);

	// Classified coloring follows the panel's visibility, not merely the presence
	// of a filter config: the config is kept on close so reopening restores it,
	// and gating on visibility means every close path reverts the map to heat
	// without its own cleanup. Must come from `activeSidebars` — SidebarTemplate's
	// `onHide` fires only for the close icon.
	const isColorFilterOpen = useIsSidebarOpen(COLLECTION_SIDEBAR_IDS.hexagonColorFilter);
	const activeClassification = isColorFilterOpen ? classificationFilter : null;

	// Gated on the settings panel's visibility, so every close path reverts the
	// map without its own cleanup — a preview cannot outlive its panel. One
	// visible consequence: SidebarTemplate delists the sidebar before awaiting
	// onHide, so the preview drops while the discard confirmation is up and
	// returns on "Keep editing". That ordering is the shared pattern the other
	// filter panels rely on, and the blink happens behind a modal.
	const isSettingsOpen = useIsSidebarOpen(COLLECTION_SIDEBAR_IDS.hexagonSettings);
	const previewColor = isSettingsOpen ? hexagonColorDraft : null;

	// Persisted filters + visible objects → per-object aggregation body, shared
	// with the DSP layer and the exports so the four surfaces cannot drift.
	const { filterBody, hasVisibleObjects } = useCollectionFilterBody(
		savedCollectionFilters,
		objects
	);

	const { cells, isFetching, error } = useCollectionHexagons(
		projectUuid,
		collectionUuid,
		resolution,
		filterBody
	);

	// classByCellId drives per-feature coloring; results build the match style.
	// Empty when no classification filter is active (heat mode).
	const { classByCellId, results } = useHexagonClassification(cells, activeClassification);

	// The layer's flat style: classified (match on matrixClass) or heat. setStyle
	// recompiles the WebGL layer, so the inactive mode's inputs are nulled out of
	// the deps — `results` is a fresh object on every classification pass (even
	// the empty one heat mode produces), which would otherwise recompile on each
	// refetch.
	const heatColor = activeClassification ? null : (previewColor ?? hexagonColor);
	const classResults = activeClassification ? results : null;
	const style = useMemo(
		() =>
			activeClassification ? buildClassifiedStyle(classResults) : buildHeatStyle(heatColor),
		[activeClassification, classResults, heatColor]
	);

	const layer = useWebGLVectorLayer(mapObject, LAYER_ID, {
		name: 'Hexagons',
		zIndex: Z_INDEX,
		visible: false,
		style,
	});

	// Build OL features with a normalized `heat` (0–1) for the opacity ramp. Heat
	// is relative to the densest cell in the current set, so the ramp always uses
	// its full range regardless of absolute counts.
	const features = useMemo(() => {
		const maxCount = cells.reduce((max, cell) => Math.max(max, cell.stats.count), 0);
		return cells
			.map(cell =>
				createFeature(cell.geometry, {
					cellId: cell.id,
					count: cell.stats.count,
					heat: maxCount > 0 ? cell.stats.count / maxCount : 0,
				})
			)
			.filter(Boolean);
	}, [cells]);

	// Mutate the layer's existing source (clear + add) rather than swapping in a
	// new VectorSource. The WebGL renderer repaints on the source's own change
	// events, so an in-place clear reliably empties the map when there are no
	// cells — replacing the source can leave the previous features rendered.
	useEffect(() => {
		if (!layer) return;
		const source = layer.getSource();
		source.clear();
		if (features.length) source.addFeatures(features);
	}, [layer, features]);

	const hasFeatures = features.length > 0;
	// Ties the checkbox to its sibling <label>: the label is a sibling rather
	// than the Checkbox's children so it stays clickable while the input is
	// disabled, which needs an explicit id to associate the two.
	const visibilityCheckboxId = useId();
	const { isEnabledByUser, setEnabledByUser, isOnMap } = useLayerVisibilityPreference(
		layer,
		hasFeatures
	);

	// Apply the style. useWebGLVectorLayer only reads its `style` option when it
	// creates the layer, so this effect owns every later change. setStyle
	// recompiles the WebGL layer, hence the memo above: it only runs on a genuine
	// change of mode, class colors, or heat hue.
	useEffect(() => {
		if (!layer) return;
		layer.setStyle(style);
	}, [layer, style]);

	// Tag each feature with its class id for the classified match style. In heat
	// mode classByCellId is empty, so matrixClass is cleared and the heat style's
	// numeric `heat` get is used instead. Only changed features are touched.
	useEffect(() => {
		if (!layer) return;
		layer.getSource().forEachFeature(feature => {
			const classId = classByCellId[feature.get('cellId')];
			if (feature.get('matrixClass') !== classId) feature.set('matrixClass', classId);
		});
	}, [layer, features, classByCellId]);

	// Selection highlight lives on its own plain VectorLayer, decoupled from the
	// WebGL heat layer (see SELECTED_STYLE). Created for this component's lifetime.
	const selectionLayerRef = useRef(null);
	useEffect(() => {
		if (!mapObject) return;
		const selectionLayer = new VectorLayer({
			source: new VectorSource(),
			style: SELECTED_STYLE,
			zIndex: SELECTED_Z_INDEX,
			properties: { customLayerId: SELECTED_LAYER_ID },
		});
		mapObject.addLayer(selectionLayer);
		selectionLayerRef.current = selectionLayer;
		return () => {
			mapObject.removeLayer(selectionLayer);
			selectionLayerRef.current = null;
		};
	}, [mapObject]);

	// Draw only the selected cell's outline, or clear when nothing is selected /
	// the selected id no longer exists in the current cell set.
	useEffect(() => {
		const selectionLayer = selectionLayerRef.current;
		if (!selectionLayer) return;
		const source = selectionLayer.getSource();
		source.clear();
		const cell = selectedCellId != null ? cells.find(c => c.id === selectedCellId) : null;
		const feature = cell?.geometry ? createFeature(cell.geometry, {}) : null;
		if (feature) source.addFeatures([feature]);
	}, [cells, selectedCellId]);

	// Hexagon-scoped selection/classification state is transient — tied to "the
	// hexagon layer is mounted" rather than a persisted preference (unlike
	// hexagonResolution and hexagonColor, which are saved to the collection and
	// survive across mounts). Without this, switching away from the layer (e.g.
	// filtering detections down to zero, or leaving orthophoto mode) leaves a
	// stale selection/classification filter in the store for the next mount.
	// The panels go with it: a sidebar left registered while its filter is cleared
	// would come back empty, with nothing to re-seed it. The settings panel is
	// absent on purpose — it owns its own teardown, since it can unmount while
	// this component stays mounted. Unmount-only, so holding the first render's
	// setters is safe.
	useEffect(() => {
		return () => {
			const { setSelectedHexagonCellId, setHexagonClassificationFilter } =
				useCollectionStore.getState();
			setSelectedHexagonCellId(null);
			setHexagonClassificationFilter(null);
			removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonColorFilter);
			removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonPanel);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Clear the selection when the layer is hidden, so the highlight doesn't
	// linger while the hexagons aren't shown, and close the card with it — it
	// describes cells that are no longer on the map. The card only ever opens on
	// demand (details button or a cell click), so it is never reopened by itself.
	// Both writes are guarded: `removeFromActiveSidebars` is recreated on every
	// context render, so an unguarded dispatch here would loop.
	const isPanelOpen = useIsSidebarOpen(COLLECTION_SIDEBAR_IDS.hexagonPanel);
	useEffect(() => {
		if (isOnMap) return;
		if (selectedCellId != null) setSelectedHexagonCellId(null);
		if (isPanelOpen) removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonPanel);
	}, [isOnMap, selectedCellId, isPanelOpen, setSelectedHexagonCellId, removeFromActiveSidebars]);

	// Cell ids are per-request lattice indices (see collectionHexagonsToCells),
	// stable only for the cell set they were assigned in. Clear the selection
	// whenever resolution or the applied filters change and trigger a
	// re-aggregation, so a stale index can't silently resolve to an unrelated
	// cell's geometry/stats in the new set.
	useEffect(() => {
		setSelectedHexagonCellId(null);
	}, [resolution, filterBody, setSelectedHexagonCellId]);

	// The card (right) and the left-hand panels coexist, so the card never evicts
	// them. It takes NO parentId on purpose: SidebarTemplate disables a sidebar
	// whose child is open (opacity + pointer-events: none), so registering it
	// under 'layerView' would freeze the Layers list — the checkbox and these
	// buttons — while it was up. The left-hand panels keep their parentId;
	// dimming the list behind them is the existing behavior.
	const openPanel = () => {
		setActiveSidebar({ sidebarId: COLLECTION_SIDEBAR_IDS.hexagonPanel });
	};
	// Closing drops the selection too: the reopen-on-select effect below is keyed
	// on the id changing, so leaving a cell selected would make re-clicking that
	// same cell a no-op.
	const closePanel = () => {
		setSelectedHexagonCellId(null);
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonPanel);
	};
	// Settings and the classification editor are both left-hand panels, so
	// opening either evicts the other — setActiveSidebar appends rather than
	// replacing same-parent siblings, the same reason CollectionDspLayer does
	// this for its own pair. The details card on the right is left alone, so cell
	// stats stay visible while cell size is adjusted here.
	const openSettings = () => {
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonColorFilter);
		setActiveSidebar({
			sidebarId: COLLECTION_SIDEBAR_IDS.hexagonSettings,
			parentId: 'layerView',
		});
	};
	const openColorFilter = () => {
		// Evicting the settings panel drops its unsaved colour with it — the
		// panel stays mounted, so its own unmount teardown does not run here.
		removeFromActiveSidebars(COLLECTION_SIDEBAR_IDS.hexagonSettings);
		useCollectionStore.getState().setHexagonColorDraft(null);
		// Seed a default classification so the panel opens with coloring active
		// rather than an empty "pick a mode" state. This is the only place a
		// default is written: opening the panel is the one moment the intent is
		// real (see the note in useHexagonFilterTemplates). Existing config is
		// left alone, so reopening restores whatever the user last had.
		const { hexagonClassificationFilter, setHexagonClassificationFilter } =
			useCollectionStore.getState();
		if (!hexagonClassificationFilter) {
			setHexagonClassificationFilter(JSON.parse(JSON.stringify(DEFAULT_PRESET)));
		}
		setActiveSidebar({
			sidebarId: COLLECTION_SIDEBAR_IDS.hexagonColorFilter,
			parentId: 'layerView',
		});
	};

	// Surface the details panel whenever a cell becomes selected, so clicking a
	// hexagon on the map reveals its stats without a separate step.
	useEffect(() => {
		if (selectedCellId != null) openPanel();
		// openPanel is a stable wrapper over context setters; intentionally keyed
		// on the selection change only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedCellId]);

	// The row lives as long as the layer; the controls disable rather than
	// unmount, so the toggle is always there to switch back on.
	if (!layer) return null;

	let disabledReason = null;
	if (!isFetching && !hasFeatures) {
		disabledReason = hasVisibleObjects
			? 'No hexagons for the current filters'
			: 'Hexagons follow the visible detections — show at least one object';
	}

	return (
		<>
			<li>
				<Flex align="center" justify="space-between" gap={8}>
					<Flex align="center" gap={8}>
						{/* The span carries the tooltip: a disabled input fires no
						    mouse events. */}
						<Tooltip title={disabledReason}>
							<span>
								<Checkbox
									id={visibilityCheckboxId}
									checked={isEnabledByUser}
									disabled={isFetching || !hasFeatures}
									onChange={e => setEnabledByUser(e.target.checked)}
								/>
							</span>
						</Tooltip>
						<Text style={{ fontSize: '0.875rem' }}>
							<label htmlFor={visibilityCheckboxId} style={{ cursor: 'pointer' }}>
								Hexagons
							</label>
						</Text>
						{/* Any re-aggregation (resolution, applied filter, or
						    visibility change) disables the toggle and shows a spinner
						    until it lands. */}
						{isFetching && <Spin size="small" aria-label="Updating hexagons" />}
						{!isFetching && error && (
							<Tooltip title="Failed to load hexagons">
								<Text type="warning" aria-label="Failed to load hexagons">
									<LuTriangleAlert />
								</Text>
							</Tooltip>
						)}
					</Flex>
					<Flex align="center" gap={2}>
						<Tooltip title="Hexagon settings">
							<Button
								type="text"
								size="small"
								icon={<LuSettings />}
								onClick={openSettings}
								disabled={!isOnMap}
								aria-label="Open hexagon settings"
							/>
						</Tooltip>
						<Tooltip title="Price estimation">
							<Button
								type="text"
								size="small"
								icon={<LuCalculator />}
								onClick={openColorFilter}
								disabled={!isOnMap}
								aria-label="Open hexagon price estimation"
							/>
						</Tooltip>
						<Tooltip title={isPanelOpen ? 'Close hexagon details' : 'Hexagon details'}>
							<Button
								type="text"
								size="small"
								icon={isPanelOpen ? <LuShrink /> : <LuExpand />}
								onClick={isPanelOpen ? closePanel : openPanel}
								disabled={!isOnMap}
								aria-label={
									isPanelOpen ? 'Close hexagon details' : 'Hexagon details'
								}
							/>
						</Tooltip>
					</Flex>
				</Flex>
			</li>

			{/* Hover tooltip + click-to-select. Gated on visibility so it only
			    interacts while the hexagons are shown. Portals its own DOM, so it
			    renders nothing at this position in the list. */}
			<CollectionHexagonTooltip enabled={isOnMap} layer={layer} cells={cells} />

			{/* Summary + per-cell details, on the right; renders nothing here until
			    opened. classByCellId/results are the map's own, so the card can
			    name the selected cell's class. */}
			<CollectionHexagonPanel cells={cells} classByCellId={classByCellId} results={results} />

			{/* Cell size + colour, saved to the collection. Renders nothing here
			    until opened. */}
			<CollectionHexagonSettings collectionUuid={collectionUuid} isFetching={isFetching} />

			{/* Matrix/simple color-filter. Portals into the modify-layers sidebar;
			    writes the active filter to the store to color cells by class.
			    classByCellId/results are passed down rather than recomputed —
			    this layer already classifies the same cells/filter for map styling. */}
			<CollectionHexagonColorFilter
				cells={cells}
				classByCellId={classByCellId}
				results={results}
				collectionName={collectionName}
				collectionUuid={collectionUuid}
				objects={objects}
				isFetching={isFetching}
			/>
		</>
	);
};

export default CollectionHexagonLayer;
